import React, { Fragment } from "react";
import styled from "styled-components";
import AnswerCell from "./AnswerCell";
import { usePlayersContext } from "./Common/Contexts";
import PlayerName from "./PlayerName";
import Slider from "./Slider";
import { HeaderCell } from "./Common/Table";
import Answer from "../Types/Answer";

interface CategoryAnswersProps {
  categories: string[];
  answers: { [socketId: string]: Answer[] };
  players: string[];
  handleonPointChange: (
    socketId: string,
    category: string,
    value: number
  ) => void;
}

const renderCell = (
  c: string,
  socketId: string,
  answers: { [socketId: string]: Answer[] },
  onPointChange: (socketId: string, category: string, value: number) => void
) => {
  const answer = answers[socketId]?.find((a) => a.category === c);
  return (
    <AnswerCell key={c + socketId} points={answer?.point}>
      {answer?.answer}
      <Slider
        points={answer?.point}
        type="range"
        min="0"
        max="2"
        value={answer?.point ?? 0}
        className="slider"
        onChange={(e) =>
          onPointChange(socketId, c, Number.parseInt(e.target.value))
        }
      />
    </AnswerCell>
  );
};

const floorMin2 = (n: number) => Math.max(2, Math.floor(n));
const getTotalNumberOfColumns = (numberOfShownColumns: number) =>
  numberOfShownColumns + getNumberOfHiddenColumns(numberOfShownColumns); // numberOfShownColumns + number of hidden players columns
const getNumberOfHiddenColumns = (numberOfShownColumns: number) =>
  numberOfShownColumns - 2;

const Container = styled.div<{ maxColumns: number }>`
  line-height: 1em;
  display: grid;
  grid-template-columns: minmax(5em, 1fr) repeat(
      ${({ maxColumns }) => maxColumns - 1},
      minmax(5em, 2fr)
    );

  // Hide all players columns except the first one by default
  div.players:not(:first-of-type) {
    display: none;
  }
  ${({ maxColumns }) => {
    var previousNumberOfColumns = maxColumns;
    var numberOfColumns = floorMin2(maxColumns / 2);
    var string = ``;
    while (true) {
      string += `@media screen and (max-width: ${
        previousNumberOfColumns * 12
      }em) {
        grid-template-columns: minmax(5em, 1fr) repeat(
          ${numberOfColumns - 1},
          minmax(5em, 2fr)
        );
        div.players:nth-of-type(${`${getTotalNumberOfColumns(
          numberOfColumns
        )}n+1`}) {
          display: unset;
        }
      }`;
      if (numberOfColumns === 2) {
        return string;
      }
      previousNumberOfColumns = numberOfColumns;
      numberOfColumns = floorMin2(numberOfColumns / 2);
    }
  }}
`;

const CategoryAnswers = ({
  categories,
  answers,
  players,
  handleonPointChange,
}: CategoryAnswersProps) => {
  const { getSocketName, players: sockets } = usePlayersContext();
  const onPointChange = (socketId: string, category: string, value: number) =>
    handleonPointChange(socketId, category, value);
  return (
    <Container maxColumns={categories.length + 1}>
      {categories.map((c) => (
        <Fragment key={c}>
          <div key={c + "playerCell"} className="players">
            <HeaderCell></HeaderCell>
            {players.map((socketId) => (
              <PlayerName
                key={c + socketId + "playerName"}
                name={getSocketName(socketId)}
                isConnected={sockets[socketId].isConnected}
                isReady={sockets[socketId].isReady}
              />
            ))}
          </div>
          <div key={c + "answerCell"}>
            <div>
              <HeaderCell title={c}>{c}</HeaderCell>
              {players.map((socketId) => {
                return renderCell(c, socketId, answers, onPointChange);
              })}
            </div>
          </div>
        </Fragment>
      ))}
    </Container>
  );
};

export default CategoryAnswers;
